import { useI18n } from "../../../i18n";
import { Goal } from "./ScenePanels";
import {
  BALANCE_GOAL_MS, DOOR_CODE, MARATHON_LEN, MAZE, MAZE_N, WATCH_SCREENS_GOAL, WATCH_STEPS_GOAL, WATCH_WARM_C, watchMissions,
} from "../lib/remotes";
import type { Balance, Door, Marathon, Maze, Watch } from "../lib/remotes";

/** 리모컨 묶음 판(lib/remotes) — 미로 정원 · 외줄타기 · 마라톤 코스 · 비밀의 문 · 손목시계 탐험 */

export function MazePanel({ s }: { s: Maze }) {
  const { t } = useI18n();
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-maze">
      <div className="pf-mb__grid" style={{ gridTemplateColumns: `repeat(${MAZE_N}, 1fr)` }} role="img" aria-label={t("student.microbit.scene.maze.name")}>
        {MAZE.flatMap((row, r) => row.split("").map((ch, c) => {
          const me = s.r === r && s.c === c;
          return (
            <span key={`${r}-${c}`} className="pf-mb__cell" data-me={me || undefined} data-testid={me ? "fs-mb-maze-me" : undefined}>
              {me ? (s.done ? "🎉" : "🐿️") : ch === "#" ? "🌳" : ch === "E" ? "🏁" : ""}
            </span>
          );
        }))}
      </div>
      <p className="pf-mb__scene-state">{t("student.microbit.scene.maze.state", { bumps: s.bumps })}</p>
      <Goal id="fs-mb-scene-goal" done={s.done} text={t("student.microbit.scene.maze.goal")} />
    </div>
  );
}

export function BalancePanel({ s }: { s: Balance }) {
  const { t } = useI18n();
  const pct = Math.min(100, (s.levelMs / BALANCE_GOAL_MS) * 100);
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-balance">
      <div className="pf-mb__bar" role="progressbar" aria-valuemin={0} aria-valuemax={BALANCE_GOAL_MS} aria-valuenow={s.levelMs}>
        <span className="pf-mb__bar-fill" style={{ width: `${pct}%` }} />
        <span className="pf-mb__bar-runner" style={{ left: `${pct}%` }}>{s.done ? "🎉" : "🤸"}</span>
      </div>
      <p className="pf-mb__scene-state" data-testid="fs-mb-balance-state">
        {t("student.microbit.scene.balance.state", { s: (s.levelMs / 1000).toFixed(1), best: (s.best / 1000).toFixed(1), falls: s.falls })}
      </p>
      <Goal id="fs-mb-scene-goal" done={s.done} text={t("student.microbit.scene.balance.goal", { s: BALANCE_GOAL_MS / 1000 })} />
    </div>
  );
}

export function MarathonPanel({ s }: { s: Marathon }) {
  const { t } = useI18n();
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-marathon">
      <div className="pf-mb__lane pf-mb__lane--long" style={{ gridTemplateColumns: `repeat(${MARATHON_LEN + 1}, minmax(0, 1fr))` }}>
        {Array.from({ length: MARATHON_LEN + 1 }, (_, i) => (
          <span key={i} className="pf-mb__lane-cell" data-me={s.pos === i || undefined}>{s.pos === i ? (s.done ? "🎉" : "🏃") : i === MARATHON_LEN ? "🏁" : ""}</span>
        ))}
      </div>
      <p className="pf-mb__scene-state" data-testid="fs-mb-marathon-state">{t("student.microbit.scene.marathon.state", { pos: s.pos, len: MARATHON_LEN, steps: s.steps })}</p>
      <Goal id="fs-mb-scene-goal" done={s.done} text={t("student.microbit.scene.marathon.goal")} />
    </div>
  );
}

const DOOR_EMOJI = { left: "⬅️", shake: "🫨", logo: "👆" } as const;

export function DoorPanel({ s }: { s: Door }) {
  const { t } = useI18n();
  const open = s.i >= DOOR_CODE.length;
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-door">
      <div className="pf-mb__row">
        <span className="pf-mb__big" data-on={open || undefined}>{open ? "🚪✨" : "🚪"}</span>
        {DOOR_CODE.map((m, i) => (
          <span key={m} className="pf-mb__chip" data-on={i < s.i || undefined} data-testid={`fs-mb-door-${m}`}>{DOOR_EMOJI[m]} {t(`student.microbit.scene.door.move.${m}`)}{i < s.i ? " ✓" : ""}</span>
        ))}
      </div>
      <p className="pf-mb__scene-state" data-testid="fs-mb-door-state">
        {open ? t("student.microbit.scene.door.open") : t(`student.microbit.scene.door.next.${DOOR_CODE[s.i]}`)}
        {s.resets ? ` · ${t("student.microbit.scene.door.resets", { n: s.resets })}` : ""}
      </p>
      <Goal id="fs-mb-scene-goal" done={open} text={t("student.microbit.scene.door.goal")} />
    </div>
  );
}

export function WatchPanel({ s, temp }: { s: Watch; temp: number | undefined }) {
  const { t } = useI18n();
  const m = watchMissions(s);
  const done = [m.modes, m.warm, m.steps].filter(Boolean).length;
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-watch">
      <div className="pf-mb__row pf-mb__row--start">
        <span className="pf-mb__chip" data-on={m.modes || undefined} data-testid="fs-mb-watch-modes">🔁 {t("student.microbit.scene.watch.modes", { n: Math.min(s.screens.length, WATCH_SCREENS_GOAL), goal: WATCH_SCREENS_GOAL })}</span>
        <span className="pf-mb__chip" data-on={m.warm || undefined} data-testid="fs-mb-watch-warm">🔥 {t("student.microbit.scene.watch.warm", { c: WATCH_WARM_C })}{m.warm ? " ✓" : ""}</span>
        <span className="pf-mb__chip" data-on={m.steps || undefined} data-testid="fs-mb-watch-steps">👣 {t("student.microbit.scene.watch.steps", { n: Math.min(s.steps, WATCH_STEPS_GOAL), goal: WATCH_STEPS_GOAL })}</span>
      </div>
      <p className="pf-mb__scene-state">{temp === undefined ? t("student.microbit.scene.watch.noTemp") : t("student.microbit.sensor.value.temp", { v: temp })}</p>
      <Goal id="fs-mb-scene-goal" done={done === 3} text={t("student.microbit.scene.watch.goal", { n: done })} />
    </div>
  );
}
